import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { processBalanceSheetData, BalanceSheetRow } from './utils/balanceSheetProcessor';

interface BalanceSheetTabProps {
  data: any[];
  companyId: number;
  selectedValuationLevel?: number;
}

const BalanceSheetTab: React.FC<BalanceSheetTabProps> = ({ data, companyId, selectedValuationLevel }) => {
  const [processedData, setProcessedData] = useState<BalanceSheetRow[]>([]);
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [currency, setCurrency] = useState<string>('USD');
  const [yearPeriodTypes, setYearPeriodTypes] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (data && data.length > 0) {
      const { rows, years } = processBalanceSheetData(data, selectedValuationLevel);
      setProcessedData(rows);
      setAvailableYears(years);

      // Extract currency and period types from raw data
      const dataArray = data[0]?.node ? data : data[0]?.edges ? data[0].edges : [];
      const currencies = new Set<string>();
      const periodTypes = new Map<string, string>();

      dataArray.forEach((item: any) => {
        const nodeData = item.node || item;
        if (nodeData.currency) currencies.add(nodeData.currency);
        if (nodeData.period && nodeData.periodType) {
          periodTypes.set(nodeData.period.toString(), nodeData.periodType);
        }
      });

      setCurrency(Array.from(currencies)[0] || 'USD');
      setYearPeriodTypes(periodTypes);
    }
  }, [data, selectedValuationLevel]);

  // Format currency values with dynamic currency
  const formatCurrency = (value: number): string => {
    if (value === 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Get period type indicator for year header
  const getPeriodTypeIndicator = (year: string): string => {
    const periodType = yearPeriodTypes.get(year);
    if (!periodType) return '';

    const firstChar = periodType.toLowerCase().charAt(0);
    switch (firstChar) {
      case 'a':
        return 'a'; // actual
      case 'p':
        return 'p'; // plan
      case 'b':
        return 'b'; // budget
      case 'f':
        return 'f'; // forecast
      default:
        return '';
    }
  };

  if (!data || data.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p={4}
        sx={{ height: '400px', textAlign: 'center' }}
      >
        <Alert severity="info" sx={{ mb: 2, maxWidth: '500px' }}>
          <Typography variant="h6" gutterBottom>
            No Balance Sheet Data Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This company does not have any Balance Sheet financial data uploaded to the system yet. To view financial
            analysis, please upload the company's financial statements through the benchmarking section.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Box mb={2}>
        <Typography variant="subtitle1" color="text.secondary">
          Balance Sheet - All values in {currency}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Assets, liabilities, and equity financial data
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  minWidth: 250,
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                  backgroundColor: 'var(--surface-raised)',
                }}
              >
                Item
              </TableCell>
              {availableYears.map(year => (
                <TableCell
                  key={year}
                  align="right"
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'var(--surface-raised)',
                    minWidth: 120,
                  }}
                >
                  {year}
                  {getPeriodTypeIndicator(year)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {processedData.map((row, index) => (
              <TableRow
                key={row.id}
                sx={{
                  backgroundColor: row.level === 0 ? 'var(--surface-raised)' : 'transparent',
                  '&:hover': {
                    backgroundColor: row.level === 0 ? 'var(--surface-overlay)' : 'var(--surface-inset)',
                  },
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: row.level === 0 || row.isTotal ? 'bold' : 'normal',
                    paddingLeft: row.level === 0 ? 1 : 3,
                    borderTop: row.isTotal ? '2px solid var(--text-primary)' : 'none',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: row.level === 0 ? 'var(--surface-raised)' : 'var(--surface-base)',
                    zIndex: 1,
                  }}
                >
                  {row.label}
                </TableCell>
                {availableYears.map(year => (
                  <TableCell
                    key={year}
                    align="right"
                    sx={{
                      fontWeight: row.level === 0 || row.isTotal ? 'bold' : 'normal',
                      color: row.values[year] < 0 ? '#d32f2f' : 'inherit',
                      borderTop: row.isTotal ? '2px solid var(--text-primary)' : 'none',
                    }}
                  >
                    {formatCurrency(row.values[year] || 0)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BalanceSheetTab;
